import { z } from 'zod';

import {
  accessIdentifierSchema,
  accessPasswordSchema,
  assertAccessOrdinal,
  sitePrefixFor,
} from './site-prefix';

/**
 * Kunden-Datenbanken: Namen, Bezeichner und die Regeln, die sie sicher machen.
 *
 * ## Warum diese Datei ausschließlich aus Einschränkungen besteht
 *
 * Auf einem Worker teilen sich alle Kunden **einen** MariaDB-Server, und die
 * phpMyAdmin-Oberfläche ist aus dem Internet erreichbar. Die einzige Trennung
 * zwischen zwei Kunden ist damit die Rechtevergabe im Datenbankserver — kein
 * Netz, kein Container, keine Datei. Fällt sie, sieht ein Kunde die Daten aller
 * anderen auf demselben Server.
 *
 * Der gefährlichste Fallstrick dabei ist eine Eigenheit von MySQL und MariaDB:
 * **In einem `GRANT` sind `_` und `%` im Datenbanknamen Platzhalter** — auch in
 * Backticks. `GRANT ALL ON `kunde_a1_1`.*` gilt deshalb für _jede_ Datenbank,
 * die auf dieses Muster passt. Der Kunde merkt nichts, sein eigener Zugriff
 * funktioniert, und der Fehler zeigt sich erst, wenn der Nachbar fremde Daten
 * sieht.
 *
 * Die Antwort darauf ist nicht, sorgfältig zu escapen, sondern Namen zu
 * erzeugen, die **kein Sonderzeichen enthalten können**: Der Zeichenvorrat ist
 * `[a-z0-9]`, und damit ist ein Platzhalter strukturell ausgeschlossen statt
 * herausgefiltert. Das Escaping kommt trotzdem dazu — dieselbe Tiefenstaffelung
 * wie `capDrop: ALL` plus feste `capAdd`-Liste.
 *
 * ## Bezeichner lassen sich nicht binden
 *
 * Kein SQL-Dialekt erlaubt einen Platzhalter für einen Tabellen- oder
 * Datenbanknamen, und MySQL/MariaDB erlauben ihn auch nicht für `IDENTIFIED BY`.
 * Jeder Wert dieser Datei landet deshalb als Text in einer Anweisung. Genau
 * deswegen sind die Schemas hier eng und die Hilfsfunktionen zum Zitieren
 * zentral: Es gibt keine zweite Stelle, an der jemand es anders machen könnte.
 */

// -- Bezeichner --------------------------------------------------------------

/**
 * Datenbank- und Benutzernamen sowie der Zeichenvorrat ihrer Passwörter.
 *
 * Beide stehen seit M4c in `site-prefix.ts` und gelten dort für **alle**
 * Zugangsarten — die Begründung dafür ist genau die Wildcard-Falle aus dem Kopf
 * dieser Datei. Weitergereicht werden sie unter den Namen, unter denen die
 * Datenbankschicht sie kennt: `quoteIdentifier` und `quotePassword` beziehen
 * sich in ihren Meldungen darauf.
 */
export {
  ACCESS_IDENTIFIER_PATTERN as DATABASE_IDENTIFIER_PATTERN,
  ACCESS_NAME_PREFIX as DATABASE_NAME_PREFIX,
  ACCESS_PASSWORD_PATTERN as DATABASE_PASSWORD_PATTERN,
} from './site-prefix';

export const databaseIdentifierSchema = accessIdentifierSchema;
export const databasePasswordSchema = accessPasswordSchema;

// -- Namensbildung -----------------------------------------------------------

/** Höchste laufende Nummer je Site. Begrenzt zugleich die Länge des Namens. */
export const MAX_DATABASE_ORDINAL = 99;

/** Wie viele Datenbanken eine Site haben darf. */
export const MAX_SITE_DATABASES = 5;

/**
 * Der Namenspräfix aller Datenbanken einer Site.
 *
 * Er ist **abgeleitet und wird nie übertragen**: Master und Agent berechnen ihn
 * beide aus der Site-ID. Damit gibt es keine Nachricht, in der er falsch stehen
 * könnte, und der Agent kann unabhängig vom Master prüfen, ob eine Spezifikation
 * ihm eine fremde Datenbank unterschieben will.
 *
 * Seit M4c teilen Datenbanken und SFTP-Konten **einen** Präfix und damit eine
 * einzige Eigentumsgrenze; die Funktion selbst steht in `site-prefix.ts`.
 */
export function databasePrefixForSite(siteId: string): string {
  return sitePrefixFor(siteId);
}

/** Name der `n`-ten Datenbank einer Site, z. B. `ks04e87b83c1d2d1`. */
export function buildDatabaseName(siteId: string, ordinal: number): string {
  return databaseIdentifierSchema.parse(
    `${databasePrefixForSite(siteId)}d${String(assertOrdinal(ordinal))}`,
  );
}

/** Benutzername zur `n`-ten Datenbank einer Site, z. B. `ks04e87b83c1d2u1`. */
export function buildDatabaseUsername(siteId: string, ordinal: number): string {
  return databaseIdentifierSchema.parse(
    `${databasePrefixForSite(siteId)}u${String(assertOrdinal(ordinal))}`,
  );
}

// `belongsToSite` — „gehört dieser Name zu dieser Site?" — steht seit M4c in
// `site-prefix.ts` und gilt dort für Datenbanken **und** SFTP-Konten. Es wird
// hier bewusst nicht zweitausgegeben: Zwei Sternexporte desselben Namens im
// Barrel wären ein Typfehler, und genau eine Fundstelle ist ohnehin der Zweck
// der Zusammenlegung.

function assertOrdinal(ordinal: number): number {
  return assertAccessOrdinal(ordinal, MAX_DATABASE_ORDINAL, 'einer Datenbank');
}

// -- Zitieren ----------------------------------------------------------------

/**
 * Setzt einen Bezeichner in Backticks.
 *
 * Die Verdopplung eines enthaltenen Backticks ist die von MySQL vorgesehene
 * Maskierung. Sie kann bei einem Wert, der `DATABASE_IDENTIFIER_PATTERN`
 * bestanden hat, gar nicht greifen — sie steht hier, damit die Funktion auch
 * dann noch richtig ist, wenn jemand sie später an einer Stelle ohne
 * Vorprüfung benutzt.
 *
 * @throws wenn der Wert kein zulässiger Bezeichner ist. Ein Notbehelf wäre hier
 * falsch: Ein Bezeichner, der die Prüfung nicht besteht, ist ein Fehler im
 * Aufrufer und keine Eingabe, die man reparieren sollte.
 */
export function quoteIdentifier(identifier: string): string {
  const checked = databaseIdentifierSchema.parse(identifier);

  return `\`${checked.replaceAll('`', '``')}\``;
}

/**
 * Setzt einen Bezeichner in Backticks **und** maskiert die Platzhalter `_` und
 * `%` — die Form, die ausschließlich in `GRANT` gebraucht wird.
 *
 * Das ist die zweite von drei unabhängigen Bremsen gegen die Wildcard-Falle
 * (erste: der Zeichenvorrat, dritte: ein Integrationstest mit zwei Sites
 * gegeneinander). Bei einem Namen aus `[a-z0-9]` bewirkt sie nichts — und genau
 * deshalb ist sie billig genug, um sie nie wieder anzufassen.
 */
const ESCAPED_UNDERSCORE = String.raw`\_`;
const ESCAPED_PERCENT = String.raw`\%`;

export function quoteGrantIdentifier(identifier: string): string {
  const escaped = databaseIdentifierSchema
    .parse(identifier)
    .replaceAll('`', '``')
    .replaceAll('_', ESCAPED_UNDERSCORE)
    .replaceAll('%', ESCAPED_PERCENT);

  return `\`${escaped}\``;
}

/**
 * Setzt ein Passwort in einfache Anführungszeichen.
 *
 * Kein allgemeines SQL-Escaping: Der Wert muss vorher
 * `DATABASE_PASSWORD_PATTERN` bestanden haben, und dieser Zeichenvorrat enthält
 * weder Anführungszeichen noch Backslash. Eine Escape-Funktion an dieser Stelle
 * wäre die Einladung, den Zeichenvorrat später aufzuweichen.
 */
export function quotePassword(password: string): string {
  return `'${databasePasswordSchema.parse(password)}'`;
}

/**
 * Der Host-Teil eines Datenbankbenutzers.
 *
 * `'%'` und nicht eine Adressliste: Ein Site-Container bekommt bei jedem Start
 * eine andere Adresse aus dem Docker-Netz, und phpMyAdmin verbindet sich von der
 * Edge aus. Eine Einschränkung hier wäre eine Liste, die bei jedem Neustart
 * falsch wird. Wer den Server überhaupt erreichen darf, entscheiden das
 * Docker-Netz und die Firewall des Workers — nicht der Host-Teil des Benutzers.
 */
export const DATABASE_USER_HOST = '%';

// -- Die Datenbank in der Spezifikation --------------------------------------

/**
 * Eine Kunden-Datenbank, wie sie in der `SiteSpec` steht.
 *
 * Das Passwort steht hier im Klartext, und das ist Absicht: Die Spezifikation
 * ist definitionsgemäß alles, was der Agent braucht — er stellt keine
 * Rückfragen. Die Folgen sind bewusst getragen: `site.spec` liegt beim Master
 * verschlüsselt (`SecretBoxService`), und eine Passwortrotation ändert den
 * Spec-Hash und läuft damit als gewöhnliches `apply` durch den M2-Apparat.
 */
export const siteDatabaseSpecSchema = z.object({
  name: databaseIdentifierSchema,
  user: databaseIdentifierSchema,
  password: databasePasswordSchema,
});

export type SiteDatabaseSpec = z.infer<typeof siteDatabaseSpecSchema>;

// -- Außensicht --------------------------------------------------------------

/**
 * Was der Betreiber über eine Datenbank zu sehen bekommt.
 *
 * **Ohne Passwort.** Es steht genau einmal in der Antwort auf das Anlegen
 * beziehungsweise auf die Rotation (`SiteDatabaseCredentials`) und danach nie
 * wieder — dieselbe Haltung wie beim Bootstrap-Token. Diese Trennung in zwei
 * Typen ist der Grund, warum ein späteres Feld in der Entität nicht
 * versehentlich hinausgeht.
 */
export const siteDatabaseViewSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: databaseIdentifierSchema,
  user: databaseIdentifierSchema,
  /**
   * Fährt der Worker bereits eine Spezifikation, die diese Datenbank nennt?
   *
   * Dieselbe Frage wie `liveOnNode` bei einer Domain und dieselbe Antwortquelle:
   * Der Node meldet den Fingerabdruck der laufenden Spezifikation. `false`
   * unmittelbar nach dem Anlegen ist der Normalfall und kein Fehler — der
   * Reconciler wendet beim nächsten Durchlauf an.
   */
  liveOnNode: z.boolean(),
  createdAt: z.string(),
});

export type SiteDatabaseView = z.infer<typeof siteDatabaseViewSchema>;

/**
 * Die vollständigen Zugangsdaten — der einzige Weg, auf dem ein Passwort den
 * Master verlässt.
 *
 * `host` und `port` sind die Sicht **eines Site-Containers** auf den Server. Für
 * phpMyAdmin gilt eine andere Adresse; sie steht bewusst nicht hier, denn der
 * Master weiß nichts über die Edge-Server und soll nichts über sie wissen.
 */
export const siteDatabaseCredentialsSchema = siteDatabaseViewSchema.extend({
  password: databasePasswordSchema,
  host: z.string(),
  port: z.number().int(),
});

export type SiteDatabaseCredentials = z.infer<typeof siteDatabaseCredentialsSchema>;
